import { useEffect, useRef, useState } from "react";
import { useLocation } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { MessageCircle, X, Send, Headset } from "lucide-react";
import { toast } from "sonner";
import api, { formatApiError } from "../lib/api";
import { useAuth } from "../context/AuthContext";

const LS_KEY = "ftc_support";

function loadSession() {
  try {
    return JSON.parse(localStorage.getItem(LS_KEY) || "null");
  } catch {
    return null;
  }
}

export default function SupportChat() {
  const { pathname } = useLocation();
  const { user } = useAuth();
  const [open, setOpen] = useState(false);
  const [session, setSession] = useState(loadSession);
  const [form, setForm] = useState({ name: "", email: "", message: "" });
  const [messages, setMessages] = useState([]);
  const [status, setStatus] = useState("open");
  const [draft, setDraft] = useState("");
  const [busy, setBusy] = useState(false);
  const bottomRef = useRef(null);

  useEffect(() => {
    if (user && !form.name) setForm((f) => ({ ...f, name: user.name || "", email: user.email || "" }));
  }, [user]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (!open || !session) return;
    let stop = false;
    const pull = () =>
      api
        .get(`/support/conversations/${session.conversation_id}/messages`, { params: { token: session.visitor_token } })
        .then(({ data }) => {
          if (stop) return;
          setMessages(data.messages);
          setStatus(data.conversation.status);
        })
        .catch(() => {});
    pull();
    const t = setInterval(pull, 5000);
    return () => {
      stop = true;
      clearInterval(t);
    };
  }, [open, session]);

  useEffect(() => {
    bottomRef.current?.scrollIntoView({ behavior: "smooth" });
  }, [messages, open]);

  if (pathname.startsWith("/support") || pathname.startsWith("/admin") || pathname.startsWith("/promo")) return null;

  const start = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      const { data } = await api.post("/support/conversations", form);
      localStorage.setItem(LS_KEY, JSON.stringify(data));
      setSession(data);
      toast.success("Chat started — an agent will reply shortly");
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail));
    } finally {
      setBusy(false);
    }
  };

  const send = async (e) => {
    e.preventDefault();
    if (!draft.trim()) return;
    setBusy(true);
    try {
      const { data } = await api.post(`/support/conversations/${session.conversation_id}/messages`, {
        token: session.visitor_token,
        text: draft.trim(),
      });
      setMessages((m) => [...m, data]);
      setDraft("");
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail));
    } finally {
      setBusy(false);
    }
  };

  const newChat = () => {
    localStorage.removeItem(LS_KEY);
    setSession(null);
    setMessages([]);
    setStatus("open");
  };

  const inputCls =
    "w-full rounded-xl border border-line bg-ink px-3.5 py-2.5 text-sm text-white outline-none transition-colors focus:border-signal";

  return (
    <>
      <button
        onClick={() => setOpen((o) => !o)}
        data-testid="support-chat-button"
        aria-label="Open support chat"
        className="fixed bottom-5 right-5 z-[90] flex h-14 w-14 items-center justify-center rounded-full bg-gradient-to-br from-[#3b82f6] to-[#8b5cf6] text-white shadow-[0_8px_30px_rgba(124,58,237,0.45)] transition-transform duration-200 hover:scale-105"
      >
        {open ? <X size={22} /> : <MessageCircle size={22} />}
      </button>

      <AnimatePresence>
        {open && (
          <motion.div
            initial={{ opacity: 0, y: 24, scale: 0.96 }}
            animate={{ opacity: 1, y: 0, scale: 1 }}
            exit={{ opacity: 0, y: 24, scale: 0.96 }}
            transition={{ duration: 0.25, ease: [0.22, 1, 0.36, 1] }}
            className="fixed bottom-24 right-5 z-[90] flex h-[480px] w-[350px] flex-col overflow-hidden rounded-2xl border border-line bg-surface shadow-2xl"
            data-testid="support-chat-panel"
          >
            <div className="flex items-center gap-3 border-b border-line bg-ink/70 px-4 py-3.5">
              <span className="flex h-9 w-9 items-center justify-center rounded-full bg-signal/15 text-signal">
                <Headset size={17} />
              </span>
              <div>
                <p className="font-display text-sm font-700 text-white">FTC Support</p>
                <p className="flex items-center gap-1.5 font-mono text-[9px] uppercase tracking-[0.15em] text-muted">
                  <i className="h-1.5 w-1.5 rounded-full bg-profit" /> Typically replies within hours
                </p>
              </div>
            </div>

            {!session ? (
              <form onSubmit={start} className="flex flex-1 flex-col gap-3 overflow-y-auto p-4" data-testid="support-start-form">
                <p className="text-sm leading-relaxed text-muted">
                  Questions about a firm, payout proof or your FTC Coins? Send us a message.
                </p>
                <input required minLength={2} placeholder="Your name" value={form.name}
                  onChange={(e) => setForm({ ...form, name: e.target.value })}
                  className={inputCls} data-testid="support-name-input" />
                <input required type="email" placeholder="Your email" value={form.email}
                  onChange={(e) => setForm({ ...form, email: e.target.value })}
                  className={inputCls} data-testid="support-email-input" />
                <textarea required minLength={2} rows={4} placeholder="How can we help?" value={form.message}
                  onChange={(e) => setForm({ ...form, message: e.target.value })}
                  className={`${inputCls} resize-none`} data-testid="support-message-input" />
                <button type="submit" disabled={busy}
                  className="btn-gradient mt-auto flex items-center justify-center gap-2 rounded-xl px-4 py-3 font-display text-sm font-700 text-white disabled:opacity-50"
                  data-testid="support-start-btn">
                  <Send size={13} /> {busy ? "Starting…" : "Start Chat"}
                </button>
              </form>
            ) : (
              <>
                <div className="flex-1 space-y-3 overflow-y-auto p-4" data-testid="support-messages">
                  {messages.map((m) => (
                    <div key={m.id} className={`flex ${m.sender === "visitor" ? "justify-end" : "justify-start"}`}>
                      <div className={`max-w-[80%] rounded-2xl px-3.5 py-2.5 text-sm leading-relaxed ${
                        m.sender === "visitor"
                          ? "rounded-br-sm bg-signal text-white"
                          : "rounded-bl-sm border border-line bg-ink text-white/90"
                      }`} data-testid={`support-msg-${m.sender}`}>
                        {m.sender === "agent" && (
                          <p className="mb-1 font-mono text-[9px] uppercase tracking-[0.15em] text-signal">{m.sender_name}</p>
                        )}
                        {m.text}
                      </div>
                    </div>
                  ))}
                  <div ref={bottomRef} />
                </div>
                {status === "closed" && (
                  <div className="border-t border-line bg-ink/60 px-4 py-2.5 text-center" data-testid="support-closed-note">
                    <p className="font-mono text-[10px] uppercase tracking-[0.15em] text-muted">
                      Conversation closed by support ·{" "}
                      <button onClick={newChat} className="text-signal underline" data-testid="support-new-chat-btn">start a new chat</button>
                    </p>
                  </div>
                )}
                <form onSubmit={send} className="flex items-center gap-2 border-t border-line p-3">
                  <input value={draft} onChange={(e) => setDraft(e.target.value)} placeholder="Type a message…"
                    className={inputCls} data-testid="support-reply-input" />
                  <button type="submit" disabled={busy || !draft.trim()}
                    className="btn-gradient flex h-10 w-10 shrink-0 items-center justify-center rounded-xl text-white disabled:opacity-50"
                    data-testid="support-send-btn" aria-label="Send message">
                    <Send size={15} />
                  </button>
                </form>
              </>
            )}
          </motion.div>
        )}
      </AnimatePresence>
    </>
  );
}
